/* ==========================================================================
   Desktop Sticky Cart drawer — controller (parallel build, desktop only).

   Owns: desktop gating, the cart-icon entry hook, open/close + scrim/ESC,
   focus management, and fetching/refreshing the drawer content section via the
   Section Rendering API. Hits the same backends as the existing flow but with
   its own code — it never mutates the existing flow's globals.

   Namespaced under window.StickyCart. Remove this file (+ its <script> tag and
   the shell render) to fully remove the feature.

   Enable flag — two layers:
     1. `enable_sticky_cart` theme setting (Theme settings → Cart) → data-flag-default.
        HARD kill-switch + base value: when off, GrowthBook is not consulted (no exposure)
        and the cart-icon click falls through to /cart. When on (and desktop) it is ALSO
        the value used whenever GrowthBook has no active opinion (see below).
     2. GrowthBook A/B (desktop only): feature `desktop-sticky-side-cart` / experiment
        `sticky-side-cart-desktop`. GrowthBook is honored ONLY when it actively buckets the
        user — a RUNNING experiment (Control → old flow, Variation 1 → sticky; this also
        fires the exposure) or an explicit force rule. When the experiment is in DRAFT /
        not running, or GB is unavailable, the theme setting governs — so with the setting
        on, the sticky cart opens even before the experiment is launched.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('StickyCart');
  if (!root) return;

  var DESKTOP_MQ = '(min-width: 769px)';

  var cfg = {
    sectionId: root.getAttribute('data-section-id'),
    triggerSel: root.getAttribute('data-cart-trigger'),
    flagKey: root.getAttribute('data-flag-key'),
    flagDefault: root.getAttribute('data-flag-default') === 'true'
  };

  var drawerEl = root.querySelector('[data-st-drawer]');
  var scrimEl = root.querySelector('[data-st-scrim]');
  var contentEl = root.querySelector('[data-st-content]');
  var loadingEl = root.querySelector('[data-st-loading]');

  var contentLoaded = false;
  var lastFocus = null;
  var openedAt = 0; // timestamp of the last drawer open (for time_in_drawer_ms)
  var busy = false; // guards concurrent cart mutations
  var refreshing = false; // a section refresh/re-render is in flight (pricing may still be settling)
  // Deferred "Proceed": if the user advances cart→review while the coupon reconcile / fees
  // recompute is still in flight, hold navigation until pricing settles, then auto-continue.
  var pendingProceed = false;
  var proceedSettleTimer = null;
  // Set when the cart changes EXTERNALLY (PDP/PLP add) while the drawer is closed,
  // so the next open re-fetches fresh content instead of showing stale state.
  var cartDirty = false;

  // Start as control (old flow); GrowthBook is authoritative and can't be read
  // synchronously. A cart-icon click BEFORE it resolves is held (not dropped to /cart) and
  // acted on once the decision lands — see bindTrigger.
  var enabled = false;
  var flagResolved = false; // GrowthBook decision has landed
  var flagPromise = null;   // resolves with the final `enabled` value

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  // True only when a sticky-cart flow is genuinely active: the feature is enabled for this
  // user (desktop + theme flag + GrowthBook bucket, per `enabled`) AND the drawer is open.
  // Gates the observable-only address analytics (bound to the site-wide address events) so
  // they don't fire on mweb, when the sticky cart is disabled/not-bucketed, or when the user
  // changes their address outside the drawer.
  function stickyFlowActive() {
    return enabled && isDesktop() && !root.hidden;
  }

  // Honor GrowthBook ONLY when it actively decides this user's bucket: a running
  // experiment (source 'experiment' — this is also where the exposure fires) or an
  // explicit force rule. When the experiment is in DRAFT / not running (source
  // 'defaultValue') or GB is unavailable, fall back to cfg.flagDefault (the theme
  // setting), so enable_sticky_cart alone opens the sticky cart pre-launch.
  //
  // We read the result via evalFeature() rather than gbCore.getFeatureValue() because
  // we need the result SOURCE: a defined boolean feature returns its own default ('false')
  // in draft, which getFeatureValue can't tell apart from an experiment's control arm.
  // (Init only calls this when the theme setting is on, so the fallback resolves to true.)
  function resolveFlag() {
    try {
      if (window.gbReady && typeof window.gbReady.then === 'function') {
        return window.gbReady.then(function (gb) {
          if (gb && typeof gb.evalFeature === 'function') {
            var res = gb.evalFeature(cfg.flagKey);
            if (res && (res.source === 'experiment' || res.source === 'force')) {
              return !!res.value;
            }
          }
          return cfg.flagDefault;
        }, function () { return cfg.flagDefault; });
      }
    } catch (e) {}
    return Promise.resolve(cfg.flagDefault);
  }

  /* ---- Persist / restore drawer state across reload + back navigation --- */
  // Remember that the drawer was open (and at which step / with which IPA product drawer) so a
  // page refresh — or a browser Back that returns to this page — reopens it in the same state.
  // Keyed to the page URL so it restores ONLY on the same page: navigating FORWARD to a new page
  // (e.g. tapping a product to its PDP) must NOT auto-open the drawer there. TTL-bounded so a
  // long-stale flag can't resurrect the drawer much later.
  var OPEN_STATE_KEY = 'stOpenState';
  var OPEN_STATE_TTL = 30 * 60 * 1000;
  function urlKey() { return location.pathname + location.search; }
  function saveOpenState(patch) {
    var s = {};
    try { s = JSON.parse(sessionStorage.getItem(OPEN_STATE_KEY)) || {}; } catch (e) {}
    if (patch) { for (var k in patch) { if (patch.hasOwnProperty(k)) s[k] = patch[k]; } }
    s.url = urlKey();
    s.ts = Date.now();
    try { sessionStorage.setItem(OPEN_STATE_KEY, JSON.stringify(s)); } catch (e) {}
  }
  function readOpenState() {
    var s; try { s = JSON.parse(sessionStorage.getItem(OPEN_STATE_KEY)); } catch (e) { return null; }
    if (!s || !s.open || s.url !== urlKey()) return null;
    if (!s.ts || (Date.now() - s.ts) > OPEN_STATE_TTL) return null;
    return s;
  }

  /* ---- Open / close ---------------------------------------------------- */
  function open(opts) {
    opts = opts || {};
    // Keep the header pill + EDD in sync with a cart address that was set without updating
    // userLocation (no-op when already in sync).
    syncHeaderLocation();
    // Normally reopen on the cart step; opts.step lets a caller land on a later step
    // once content is ready (e.g. 'review' after a post-login reopen — see init).
    var targetStep = opts.step || 'cart';
    saveOpenState({ open: true, step: targetStep });
    lastFocus = document.activeElement;
    currentStep = 'cart';
    openedAt = Date.now();
    root.hidden = false;
    // Force reflow so the transition runs from the translated state.
    void root.offsetWidth;
    root.classList.add('is-open');
    document.addEventListener('keydown', onKeydown);
    if (drawerEl) drawerEl.focus();
    function landOnStep() { if (targetStep && targetStep !== 'cart') goToStep(targetStep); }
    if (!contentLoaded) {
      fetchContent().then(function () { fireOpened(); landOnStep(); });
    } else if (cartDirty) {
      // Cart was changed elsewhere (PDP/PLP add or qty +/-) since we last rendered.
      // Show the loader to cover the stale content until the refresh lands, so the user
      // never sees the old cart flash before the update.
      cartDirty = false;
      if (loadingEl) loadingEl.hidden = false;
      refresh().then(function () {
        if (loadingEl) loadingEl.hidden = true; // refresh failure swallows its error → reveal content anyway
        fireCartView();
        fireOpened();
        landOnStep();
      });
    } else {
      // Clean reopen: content is already rendered but may have been left on a later step
      // (e.g. closed while on Review). bindContent isn't re-run here, so reset the visible
      // step/stepper to match currentStep ('cart') — otherwise the drawer reopens showing the
      // stale Review panel and the Cart stepper tab no-ops (currentStep already === target).
      applyStep(currentStep);
      fireCartView();
      fireOpened();
      landOnStep();
    }
  }

  function fireCartView() {
    if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.viewCart === 'function') {
      window.StickyCartAnalytics.viewCart(contentEl, 'Cart');
    }
  }

  // Drawer shown with content ready → Sticky Cart Opened (+ initial cart Step Viewed).
  function fireOpened() {
    var A = window.StickyCartAnalytics;
    if (!A) return;
    if (typeof A.opened === 'function') A.opened('add_to_cart');
    if (typeof A.stepViewed === 'function') A.stepViewed('cart', 'forward');
  }

  function close() {
    var A = window.StickyCartAnalytics;
    if (!root.hidden && A && typeof A.closed === 'function') {
      A.closed(currentStep, openedAt ? Date.now() - openedAt : 0);
    }
    // Drop any deferred Proceed so it can't auto-fire / leave the CTA stuck on reopen.
    pendingProceed = false;
    if (proceedSettleTimer) { clearTimeout(proceedSettleTimer); proceedSettleTimer = null; }
    // Explicit close → don't restore on the next reload of this page.
    saveOpenState({ open: false, ipaVId: null });
    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);
    var onEnd = function (e) {
      if (e && e.target !== drawerEl) return;
      root.hidden = true;
      if (drawerEl) drawerEl.removeEventListener('transitionend', onEnd);
    };
    if (drawerEl) {
      drawerEl.addEventListener('transitionend', onEnd);
    } else {
      root.hidden = true;
    }
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') close();
  }

  /* ---- Content fetch / refresh (Section Rendering API) ----------------- */
  function sectionUrl() {
    return '/?section_id=' + encodeURIComponent(cfg.sectionId);
  }

  function applyContent(html) {
    contentEl.innerHTML = html;
    contentLoaded = true;
    if (loadingEl) loadingEl.hidden = true;
    bindContent();
  }

  function fetchContent() {
    if (loadingEl) loadingEl.hidden = false;
    return fetch(sectionUrl(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        applyContent(html);
        fireCartView(); // view_cart on first open
      })
      .catch(function (err) {
        if (window.console) console.error('[StickyCart]', err);
        if (loadingEl) loadingEl.hidden = true;
        // Surface a clear failure rather than silently showing an empty drawer:
        // fall back to the existing full-page cart so the user is never stuck.
        window.location.href = '/cart';
      });
  }

  // Re-fetch + replace content (used as a fallback when a mutation response
  // doesn't carry the section HTML).
  function refresh() {
    if (!contentLoaded) return fetchContent();
    refreshing = true;
    return fetch(sectionUrl(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('section refresh failed: ' + r.status);
        return r.text();
      })
      .then(applyContent)
      .catch(function (err) {
        if (window.console) console.error('[StickyCart]', err);
      })
      .then(function () {
        refreshing = false;
        signalPricingMaybeSettled();
      });
  }

  // True when the cart, applied coupon, AND fees/total are done updating — i.e. the price shown is
  // final. Gates Proceed → Review so the user never lands on a half-computed price.
  //
  // feesBusy (StickyCartFees.isPending()) is now SIGNATURE-AWARE: it's true only when the price for
  // the CURRENT cart state hasn't settled yet (first load / cart changed), NOT on routine re-fetches
  // of an unchanged cart. So this blocks Proceed (with a loader) only when the price genuinely hasn't
  // loaded — once it has, Proceed is instant even while a background re-fetch happens.
  function pricingSettled() {
    var couponBusy = window.StickyCartCoupon && typeof window.StickyCartCoupon.isReconciling === 'function' && window.StickyCartCoupon.isReconciling();
    // NOTE: we intentionally do NOT gate on the fees fetch (StickyCartFees.isPending()). For a
    // large cart the /order/fees call is slow, and gating on it made Proceed sit on a loader even
    // though the cart was otherwise idle. The Review bill is a live clone that re-syncs its total /
    // fees / savings from the cart bill on every 'stcart:pricing-updated' (see refreshReviewBill in
    // sticky-cart-checkout.js), so it's safe to advance before fees finalize — the price updates in
    // place a moment later. We still hold for an in-flight cart mutation / coupon reconcile, which
    // is the actual stale-price race this gate exists for.
    return !busy && !refreshing && !couponBusy;
  }

  // Coupon/fees/refresh each call this (directly or via the 'stcart:pricing-updated' event)
  // when they finish, so a deferred Proceed can resume the moment everything has settled.
  function signalPricingMaybeSettled() {
    try { document.dispatchEvent(new Event('stcart:pricing-updated')); } catch (e) {}
  }

  // Put the Proceed CTA into / out of a loading state: the label + arrow are hidden and a
  // spinner is shown (CSS: .st-cart__cta.is-loading) — no text swap.
  function setProceedPending(on) {
    var btn = contentEl.querySelector('[data-st-proceed]');
    if (!btn) return;
    btn.disabled = !!on;
    btn.classList.toggle('is-loading', !!on);
  }

  // Resume a deferred Proceed once pricing has settled (and we're still on the cart step).
  function tryResumeProceed() {
    if (!pendingProceed) return;
    if (root.hidden || currentStep !== 'cart') { // drawer closed / step changed → drop it
      pendingProceed = false;
      if (proceedSettleTimer) { clearTimeout(proceedSettleTimer); proceedSettleTimer = null; }
      setProceedPending(false);
      return;
    }
    if (!pricingSettled()) return;
    pendingProceed = false;
    if (proceedSettleTimer) { clearTimeout(proceedSettleTimer); proceedSettleTimer = null; }
    setProceedPending(false);
    goToStep('review');
  }
  document.addEventListener('stcart:pricing-updated', tryResumeProceed);

  /* ---- Cart mutations (single round-trip via the AJAX sections param) -- */
  function setBusy(on) {
    busy = on;
    contentEl.classList.toggle('is-busy', on);
    if (!on) signalPricingMaybeSettled();
  }

  // Keep every header cart-icon count (duplicated #CartBubble) in sync.
  function syncCartCount(count) {
    if (count == null) return;
    var nums = document.querySelectorAll('.cart-link__bubble-num');
    var i;
    for (i = 0; i < nums.length; i++) nums[i].textContent = count;
    var bubbles = document.querySelectorAll('.cart-link__bubble');
    for (i = 0; i < bubbles.length; i++) {
      bubbles[i].classList.toggle('cart-link__bubble--visible', count > 0);
    }
  }

  // Tell the rest of the page the cart changed (so PDP/PLP steppers, the sticky
  // ATC bar and any cart:updated consumers re-sync). Mirrors the theme's canonical
  // `cart:updated` event (assets/theme.js) + keeps window.shopifyCart fresh. The
  // extra `sticky-cart:line-changed` carries the specific line so an open PDP/PLP
  // can target the matching card without diffing the whole cart.
  function broadcastCartChange(cart, changed) {
    if (!cart) return;
    // Keep the header cart bubble in sync on EVERY broadcast path — not just the
    // controller's own changeLine/clearOOS, but also module-driven removals (gift-unlock
    // removeTheseFromCart, surprise-gift, coupon reconcile) that route through
    // fetchCartThenBroadcast → here.
    if (typeof cart.item_count === 'number') syncCartCount(cart.item_count);
    try { window.shopifyCart = cart; } catch (e) {}
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, source: 'sticky-cart' } }));
    if (changed) {
      document.dispatchEvent(new CustomEvent('sticky-cart:line-changed', {
        detail: {
          variantId: changed.variantId || null,
          productId: changed.productId || null,
          key: changed.key || null,
          quantity: changed.quantity != null ? changed.quantity : null,
          cart: cart
        }
      }));
      // PLP grid (Findify/Unbxd) cards are JS-rendered (assets/edd.js), not a Liquid
      // snippet, so they don't observe cart:updated. Reuse the grid's OWN per-variant
      // updater — the same global edd.js calls after its +/- actions — so an open
      // collection page reflects the new quantity on the matching card.
      if (changed.variantId && typeof window.updateQuantityControlsForVariant === 'function') {
        try { window.updateQuantityControlsForVariant(changed.variantId); } catch (e) {}
      }
    }
  }

  // For mutations whose response isn't the full cart (gift add / rx attach): fetch
  // the latest cart once, then broadcast.
  function fetchCartThenBroadcast() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { broadcastCartChange(cart); })
      .catch(function () {});
  }

  function changeLine(lineKey, quantity, meta) {
    if (busy || !lineKey) return;
    setBusy(true);
    // Capture the line's product/variant id BEFORE mutating, so we can still
    // broadcast them when the line is removed (quantity 0, gone from the response).
    var row = contentEl.querySelector('.st-cart__item[data-line-key="' + lineKey + '"]');
    var changed = {
      key: lineKey,
      variantId: row ? row.getAttribute('data-variant-id') : null,
      productId: row ? row.getAttribute('data-product-id') : null,
      quantity: quantity
    };
    // Analytics snapshot captured pre-mutation (the row is gone after a removal).
    var A = window.StickyCartAnalytics;
    var snap = (meta && A && typeof A.productFromEl === 'function') ? A.productFromEl(row) : null;
    if (snap && row) { snap.is_rx_item = row.getAttribute('data-rx') === 'true'; snap.is_free_gift = row.getAttribute('data-free-gift') === 'true'; }
    var body = {
      id: lineKey,
      quantity: quantity,
      sections: cfg.sectionId,
      sections_url: window.location.pathname
    };
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data && data.sections && data.sections[cfg.sectionId]) {
          applyContent(data.sections[cfg.sectionId]);
        } else {
          return refresh();
        }
        if (data && typeof data.item_count === 'number') syncCartCount(data.item_count);
        // `data` is the full cart (change.js response) — notify the rest of the page.
        broadcastCartChange(data, changed);
        // Spec line-item analytics (fired post-render so cart totals are current).
        if (meta && snap && A) {
          if (meta.next === 0 && typeof A.itemRemoved === 'function') {
            A.itemRemoved(snap, meta.prev);
          } else if (typeof A.qtyChanged === 'function') {
            A.qtyChanged(meta.next > meta.prev, row, meta.prev, meta.next);
          }
        }
      })
      .catch(function (err) {
        if (window.console) console.error('[StickyCart] changeLine', err);
        refresh();
      })
      .then(function () {
        setBusy(false);
      });
  }

  // OOS line keys, read from the server-rendered OOS card. The single source of
  // truth used by checkout/coupon/gift-unlock to filter /cart.js items to in-stock.
  function getOOSKeys() {
    var keys = [];
    if (!contentEl) return keys;
    var nodes = contentEl.querySelectorAll('[data-st-oos-items] .st-cart__item, .st-cart__item[data-oos="true"]');
    for (var i = 0; i < nodes.length; i++) {
      var k = nodes[i].getAttribute('data-line-key');
      if (k && keys.indexOf(k) < 0) keys.push(k);
    }
    return keys;
  }

  // Remove ALL out-of-stock lines in one /cart/update.js round-trip (the OOS card ×).
  function clearOOS() {
    if (busy) return;
    var nodes = contentEl.querySelectorAll('[data-st-oos-items] .st-cart__item');
    var updates = {};
    var variantIds = [];
    var oosSnaps = [];
    var count = 0;
    for (var i = 0; i < nodes.length; i++) {
      var key = nodes[i].getAttribute('data-line-key');
      if (!key) continue;
      updates[key] = 0;
      count++;
      var vid = nodes[i].getAttribute('data-variant-id');
      if (vid) variantIds.push(vid);
      // Capture each OOS line for the per-item removal event (gone after the update).
      oosSnaps.push({
        product_id: nodes[i].getAttribute('data-product-id'),
        variant_id: vid || null,
        sku_id: nodes[i].getAttribute('data-skuid'),
        product_name: (nodes[i].querySelector('.st-cart__item-name') || {}).textContent || null
      });
    }
    if (!count) return;
    setBusy(true);
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates, sections: cfg.sectionId, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.sections && data.sections[cfg.sectionId]) {
          applyContent(data.sections[cfg.sectionId]);
        } else {
          return refresh();
        }
        if (data && typeof data.item_count === 'number') syncCartCount(data.item_count);
        broadcastCartChange(data);
        // Spec: one OOS Item Removed per cleared line (remaining count counts down).
        if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.oosItemRemoved === 'function') {
          oosSnaps.forEach(function (s, idx) { window.StickyCartAnalytics.oosItemRemoved(s, oosSnaps.length - 1 - idx); });
        }
        // Reflect each removed line on an open PLP/PDP grid (no single changed line).
        if (typeof window.updateQuantityControlsForVariant === 'function') {
          variantIds.forEach(function (v) { try { window.updateQuantityControlsForVariant(v); } catch (e) {} });
        }
      })
      .catch(function (err) {
        if (window.console) console.error('[StickyCart] clearOOS', err);
        refresh();
      })
      .then(function () { setBusy(false); });
  }

  // Rewards lines are limited to one unit (cart-price.liquid:47-52). If any rendered
  // line is tagged rewards with qty > 1, force it to 1 in a single round-trip. The
  // re-render re-runs this with qty 1 → no-op, so there's no loop.
  function normalizeRewards() {
    if (busy) return;
    var nodes = contentEl.querySelectorAll('.st-cart__item[data-rewards="true"]');
    var updates = {};
    var changed = 0;
    for (var i = 0; i < nodes.length; i++) {
      var key = nodes[i].getAttribute('data-line-key');
      var qty = parseInt(nodes[i].getAttribute('data-qty'), 10) || 0;
      if (key && qty > 1) { updates[key] = 1; changed++; }
    }
    if (!changed) return;
    setBusy(true);
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates, sections: cfg.sectionId, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.sections && data.sections[cfg.sectionId]) applyContent(data.sections[cfg.sectionId]);
        else return refresh();
        if (data && typeof data.item_count === 'number') syncCartCount(data.item_count);
        broadcastCartChange(data);
      })
      .catch(function (err) { if (window.console) console.error('[StickyCart] normalizeRewards', err); refresh(); })
      .then(function () { setBusy(false); });
  }

  // BXGY line badge copy lives on the applied coupon (localStorage.bxgyData), mirroring
  // the existing cart. Fill the rendered badge(s); hide if there's no bxgy coupon.
  function fillBxgyBadges() {
    var badges = contentEl.querySelectorAll('[data-st-bxgy-tag]');
    if (!badges.length) return;
    var data = null;
    try { data = JSON.parse(localStorage.getItem('bxgyData')); } catch (e) {}
    var text = data && (data.bxgyDeskText || data.bxgyMobText);
    for (var i = 0; i < badges.length; i++) {
      if (text) { badges[i].textContent = text; badges[i].hidden = false; }
      else badges[i].hidden = true;
    }
  }

  /* ---- Step machine (Cart → Address → Review → Payment) ---------------- */
  var STEPS = ['cart', 'address', 'review', 'payment'];
  var currentStep = 'cart';

  function stepIndex(step) {
    var i = STEPS.indexOf(step);
    return i < 0 ? 0 : i;
  }

  function goToStep(step) {
    if (STEPS.indexOf(step) < 0) return;
    var from = currentStep;
    currentStep = step;
    saveOpenState({ step: step });
    applyStep(step);
    var body = contentEl.querySelector('[data-st-body]');
    if (body) body.scrollTop = 0;

    // Funnel analytics on forward transitions. begin_checkout fires when entering
    // address, OR on the cart→review jump (the Address step is now bypassed).
    if (window.StickyCartAnalytics && stepIndex(step) > stepIndex(from)) {
      if (step === 'address' || (step === 'review' && from === 'cart')) {
        window.StickyCartAnalytics.beginCheckout(contentEl);
      }
      if (step === 'review') {
        window.StickyCartAnalytics.viewCart(contentEl, 'Review');
        window.StickyCartAnalytics.cartReviewed(contentEl);
      }
    }

    // Spec funnel: Step Viewed on EVERY stepper change (forward + back).
    if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.stepViewed === 'function') {
      window.StickyCartAnalytics.stepViewed(step, stepIndex(step) >= stepIndex(from) ? 'forward' : 'back');
    }
  }

  // Reflect the current step on the freshly rendered content (stepper state,
  // visible panel, footer CTA + address line). Called from bindContent.
  function applyStep(step) {
    var idx = stepIndex(step);

    var steps = contentEl.querySelectorAll('[data-st-stepper] [data-step]');
    var i;
    for (i = 0; i < steps.length; i++) {
      var sIdx = stepIndex(steps[i].getAttribute('data-step'));
      var isActive = sIdx === idx;
      var isDone = sIdx < idx;
      steps[i].classList.toggle('is-active', isActive);
      steps[i].classList.toggle('is-done', isDone);
      // Swap the icon by state: passed → orange tick, active → orange, future → black.
      var icon = steps[i].querySelector('.st-cart__step-icon');
      if (icon) {
        var src = isDone ? icon.getAttribute('data-icon-done')
          : isActive ? icon.getAttribute('data-icon-active')
          : icon.getAttribute('data-icon-default');
        if (src && icon.getAttribute('src') !== src) icon.setAttribute('src', src);
      }
    }

    // Connector lines: orange up to and including the active step's segment.
    var lines = contentEl.querySelectorAll('[data-st-stepper] .st-cart__step-line');
    for (i = 0; i < lines.length; i++) {
      lines[i].classList.toggle('is-active', i <= idx);
    }

    var panels = contentEl.querySelectorAll('[data-st-step-panel]');
    for (i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-st-step-panel') !== step;
    }

    renderAddress();
    updateFooterForStep(step);

    if (step === 'review' && window.StickyCartCheckout) {
      window.StickyCartCheckout.renderReview(contentEl);
    }
    if (step === 'payment' && window.StickyCartCheckout) {
      window.StickyCartCheckout.renderPayment(contentEl);
    }
  }

  function updateFooterForStep(step) {
    var footer = contentEl.querySelector('[data-st-footer]');
    var proceed = contentEl.querySelector('[data-st-proceed]');
    var addressLine = contentEl.querySelector('[data-st-footer-address]');
    var locPrompt = contentEl.querySelector('[data-st-footer-locprompt]');
    var setLoc = contentEl.querySelector('[data-st-footer-setloc]');
    var footerRow = contentEl.querySelector('[data-st-footer] .st-cart__footer-row');
    var addr = getStoredAddress();

    // The payment step has no footer at all (methods are selected inline).
    if (footer) footer.hidden = (step === 'payment');
    if (step === 'payment') return;

    function showLocPrompt(on) {
      if (locPrompt) locPrompt.hidden = !on;
      if (footerRow) footerRow.classList.toggle('st-cart__footer-row--locprompt', on);
    }

    if (!proceed) return;
    var label = proceed.querySelector('.st-cart__cta-label');
    function setLabel(text) {
      if (label) label.textContent = text;
      else proceed.firstChild && (proceed.firstChild.nodeValue = text);
    }

    proceed.hidden = false;

    if (step === 'cart') {
      // States:
      //   • precise address (lat/long)                 → address line + "Proceed to pay"
      //   • LOGGED-IN address with NO lat/long          → address line + "set location on map"
      //                                                   notice + "Confirm address"
      //   • guest with no precise address, or no address → location prompt + "Choose address at
      //                                                   next step" (guests never see Confirm)
      var ready = proceedReady();
      // The "Confirm address" / set-location-on-map flow is LOGGED-IN only. A guest whose location
      // lacks lat/long keeps the original "Choose address at next step" prompt — they set their
      // precise location through that flow, not the in-footer confirm.
      var confirmState = !ready && !!addr && isLoggedIn();
      var hasAddr = ready || confirmState; // show the address line + (for confirm) the notice
      const icon = proceed.querySelector('.st-cart__cta-icon');
      // locprompt TEXT when there's no address-line to show; full-width CTA (no price) whenever
      // the CTA isn't "Proceed to pay".
      if (locPrompt) locPrompt.hidden = hasAddr;
      if (footerRow) footerRow.classList.toggle('st-cart__footer-row--locprompt', !ready);
      if (addressLine) addressLine.hidden = !hasAddr;
      if (setLoc) setLoc.hidden = !confirmState; // notice only for the logged-in confirm state
      if (ready) {
        setLabel('Proceed to pay');
        if (icon) icon.style.display = 'block';
      } else if (confirmState) {
        setLabel('Confirm address');
        if (icon) icon.style.display = 'none';
      } else {
        setLabel('Choose address at next step');
        if (icon) icon.style.display = 'none';
        renderLocPrompt(locPrompt);
      }
      // A deferred Proceed survived a re-render → keep the new button in its loading state.
      if (pendingProceed) { setProceedPending(true); }
      return;
    }

    // address / review steps: address line visible if we have one.
    if (addressLine) addressLine.hidden = !addr;
    if (setLoc) setLoc.hidden = true;
    showLocPrompt(false);
  }

  // Fill the cart-step location prompt. Detected location → "new location" copy;
  // nothing detected → a generic add-address prompt.
  function renderLocPrompt(locPrompt) {
    if (!locPrompt) return;
    var titleEl = locPrompt.querySelector('[data-st-footer-locprompt-title]');
    var addrEl = locPrompt.querySelector('[data-st-footer-locprompt-addr]');
    var text = detectedLocationText();
    if (text) {
      if (titleEl) titleEl.textContent = 'You seem to be in a new location!';
      if (addrEl) { addrEl.textContent = text; addrEl.hidden = false; }
    } else {
      if (titleEl) titleEl.textContent = 'Add a delivery address';
      if (addrEl) { addrEl.textContent = 'So our rider can reach you'; addrEl.hidden = false; }
    }
  }

  // Stepper navigation. Backward to an earlier step is free; forward applies the same gates as
  // Proceed so a user can't skip address/login. Address is always edited via the location modal.
  function navigateToStep(target) {
    if (!target || target === currentStep) return;
    if (target === 'address') { openLocationPicker('stepper'); return; }
    if (stepIndex(target) < stepIndex(currentStep)) { goToStep(target); return; }
    // Forward → Review/Payment require a PRECISE address (confirms on the map if it has no
    // lat/long, else opens the picker).
    if (!requirePreciseAddress('stepper')) return;
    // Payment additionally requires login (multipass reload → reopen at Review).
    if (target === 'payment' && !isLoggedIn()) {
      try { sessionStorage.setItem('stReopenStep', 'review'); } catch (e) {}
      openCheckoutLogin();
      return;
    }
    goToStep(target);
  }

  function onProceed() {
    // Spec primary funnel event: footer CTA tapped (any step).
    if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.proceedToPay === 'function') {
      var pAddr = getStoredAddress();
      window.StickyCartAnalytics.proceedToPay(
        currentStep,
        hasConfirmedOrderAddress(),
        pAddr ? (pAddr.zip || pAddr.pincode || null) : null
      );
    }
    if (currentStep === 'cart') {
      // No confirmed delivery address yet → open the location picker (branched by
      // user type). Once an address is selected, "Proceed to pay" jumps straight to
      // Review — the in-drawer Address step is bypassed (address is edited via the modal).
      // Need a PRECISE address (with lat/long). None → picker; address without coords → open the
      // map to set its location ("Confirm address"); either way don't advance until it's pinned.
      if (!requirePreciseAddress('proceed_to_pay')) return;
      // Don't advance with in-flux pricing (e.g. a coupon being auto-removed after a qty
      // change): hold until the coupon + fees settle, showing a loader, then auto-continue.
      if (!pricingSettled()) {
        pendingProceed = true;
        setProceedPending(true);
        if (proceedSettleTimer) clearTimeout(proceedSettleTimer);
        // Safety net: never trap the user if a settle signal never arrives (Review also
        // re-renders itself once pricing settles).
        proceedSettleTimer = setTimeout(function () {
          if (!pendingProceed) return;
          pendingProceed = false;
          setProceedPending(false);
          if (!root.hidden && currentStep === 'cart') goToStep('review');
        }, 4000);
        return;
      }
      goToStep('review');
    } else if (currentStep === 'review') {
      // Payment requires login — logged-out users get the login modal first. Login reloads
      // the page (multipass), so persist a one-shot intent to reopen the drawer at Review.
      if (!isLoggedIn()) {
        try { sessionStorage.setItem('stReopenStep', 'review'); } catch (e) {}
        openCheckoutLogin();
        return;
      }
      goToStep('payment');
    }
  }

  /* ---- Address (reuses the global window.showAddressMap picker) --------- */
  function getStoredAddress() {
    var raw =
      sessionStorage.getItem('order_shipping_address') ||
      sessionStorage.getItem('guestAddress');
    if (raw) {
      try { return JSON.parse(raw); } catch (e) {}
    }
    var loc;
    try { loc = JSON.parse(localStorage.getItem('userLocation')); } catch (e) {}
    if (loc && (loc.pincode || loc.area)) {
      return {
        name: loc.name || '',
        phone: loc.phone || '',
        address1: loc.area || loc.house || '',
        address2: loc.fullAddress || '',
        city: loc.city || '',
        province: loc.state || '',
        zip: loc.pincode || '',
        latitude: loc.latitude || null,
        longitude: loc.longitude || null,
        is_confirmed: !!(loc.latitude && loc.longitude)
      };
    }
    return null;
  }

  function isAddressConfirmed(addr) {
    return !!(addr && addr.latitude && addr.longitude && addr.is_confirmed !== false);
  }

  // A delivery address is "confirmed for this order" once it's been saved to
  // sessionStorage by the address picker this session (NOT a merely-detected
  // userLocation, which getStoredAddress() also surfaces). Drives the cart-step
  // footer: unconfirmed → location prompt; confirmed → address + Proceed to pay.
  function hasConfirmedOrderAddress() {
    var raw = sessionStorage.getItem('order_shipping_address') || sessionStorage.getItem('guestAddress');
    if (!raw) return false;
    try {
      var a = JSON.parse(raw);
      return !!(a && a.is_confirmed !== false && (a.zip || (a.latitude && a.longitude)));
    } catch (e) { return false; }
  }

  function orderAddress() {
    var raw = sessionStorage.getItem('order_shipping_address') || sessionStorage.getItem('guestAddress');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  // A precise, ready-to-checkout address has confirmed COORDINATES. An address with only a
  // pincode/text but no lat/long needs a one-time "set location on map" (the Confirm address flow).
  function proceedReady() {
    var a = orderAddress();
    return !!(a && a.latitude && a.longitude && a.is_confirmed !== false);
  }

  // Cart → forward gate. Precise address → proceed (true). Otherwise kick off the right flow and
  // return false: an address without coordinates opens the map DIRECTLY to set its location
  // (→ form → save adds lat/long); no address opens the picker.
  function requirePreciseAddress(entry) {
    if (proceedReady()) return true;
    var addr = getStoredAddress();
    // Logged-in "Confirm address": open the map directly on the current address to set lat/long.
    // Guests (and the no-address case) use the normal picker — the original "Choose address at
    // next step" behavior — so the logged-out flow is unchanged.
    if (isLoggedIn() && addr && typeof window.showAddressMap === 'function') {
      window.showAddressMap(addr);
    } else {
      openLocationPicker(entry);
    }
    return false;
  }

  // Detected (not yet confirmed) location text for the cart-step prompt.
  function detectedLocationText() {
    var loc;
    try { loc = JSON.parse(localStorage.getItem('userLocation')); } catch (e) {}
    if (loc && loc.fullAddress) return loc.fullAddress;
    var addr = getStoredAddress();
    return addr ? formatAddressLine(addr) : '';
  }

  // Logged-in flag (server truth, emitted on the shell root by sticky-cart-shell.liquid).
  function isLoggedIn() {
    return root.getAttribute('data-logged-in') === 'true';
  }

  // Payment requires login — open the sticky-cart login/OTP modal (parallel build).
  function openCheckoutLogin() {
    if (window.StickyCartLogin && typeof window.StickyCartLogin.open === 'function') {
      window.StickyCartLogin.open();
      return;
    }
    window.location.href = '/account/login';
  }

  // Open the right existing location modal by LOGIN state:
  //   logged in  → "Select Location Manually" (saved addresses / manual / current loc)
  //   logged out → map "Confirm & review address" → form → save (saved LOCALLY, no
  //                backend — the modal skips the /customer/address call for guests).
  // Used by both the cart-step CTA and the "Change" / "Add address" triggers.
  function openLocationPicker(entry) {
    // Spec (observable): approximate Address List Viewed when the sticky cart opens the picker.
    if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.addressListViewed === 'function') {
      var ploc; try { ploc = JSON.parse(localStorage.getItem('userLocation')); } catch (e) {}
      window.StickyCartAnalytics.addressListViewed(null, !!(ploc && ploc.pincode), (ploc && ploc.pincode) || null, entry || 'back_navigation');
    }
    if (isLoggedIn() && typeof window.openManualSelectionModal === 'function') {
      window.openManualSelectionModal();
      return;
    }
    // Guest: the previous customer's identity + saved address linger in storage after logout and
    // would pre-fill the picker (name/phone via getCustomerData, address via getLocationData) —
    // a logged-out user must get a fresh picker, so clear that stale data first.
    if (!isLoggedIn()) clearStaleCustomerIdentity();
    if (typeof window.showAddressMap === 'function') {
      // Hand the picker the CURRENT saved address so re-editing reflects the latest values. The
      // picker only (re)populates its form fields when it's passed an address — called bare it
      // keeps the stale DOM values from the previous open, so an updated address wasn't shown.
      window.showAddressMap(getStoredAddress() || null);
      return;
    }
    window.location.href = '/cart?page=address';
  }

  // Remove logged-in-only identity + saved-address data that survives a logout, so a guest
  // never inherits the previous customer's pre-filled details. A guest's OWN entered address
  // (no backend id) is preserved.
  function clearStaleCustomerIdentity() {
    try {
      ['_customerId', '_customerFirstName', '_customerLastName', '_customerPhone', 'selectedAddressId']
        .forEach(function (k) { localStorage.removeItem(k); });
    } catch (e) {}
    try {
      var ul = JSON.parse(localStorage.getItem('userLocation'));
      if (ul && ul.addressId) localStorage.removeItem('userLocation');
    } catch (e) {}
    try {
      var osa = JSON.parse(sessionStorage.getItem('order_shipping_address'));
      if (osa && osa.id) sessionStorage.removeItem('order_shipping_address');
    } catch (e) {}
  }

  function formatAddressLine(a) {
    return [a.address1, a.address2, a.city, a.province, a.zip]
      .filter(function (p) { return p && String(p).trim(); })
      .join(', ');
  }

  function addressName(a) {
    if (a.name) return a.name;
    return [a.first_name, a.last_name].filter(Boolean).join(' ');
  }

  // Populates the footer delivery-address line and wires the "Change" trigger.
  // (There's no in-drawer Address panel anymore — address is edited via the modal.)
  function renderAddress() {
    var addr = getStoredAddress();

    // Footer address line text.
    if (addr) {
      setSel(contentEl, '[data-st-footer-address-name]', addressName(addr) || 'Delivery address');
      setSel(contentEl, '[data-st-footer-address-line]', formatAddressLine(addr));
    }

    // Wire the footer "Change" trigger (opens the address modal).
    var triggers = contentEl.querySelectorAll('[data-st-address-change]');
    var i;
    for (i = 0; i < triggers.length; i++) {
      triggers[i].addEventListener('click', openAddressPicker);
    }
  }

  // "Change" / "Add address" / address-step picker — route through the same
  // login-branched opener (logged in → manual modal; logged out → map).
  function openAddressPicker() {
    if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.footerAddressChange === 'function') {
      var a = getStoredAddress();
      window.StickyCartAnalytics.footerAddressChange(currentStep, hasConfirmedOrderAddress(), a ? (a.zip || a.pincode || null) : null);
    }
    openLocationPicker('footer_change');
  }

  // The global address picker fires this on save. Persist a normalized address
  // (for display + the order payload), refresh, and advance if we're waiting on it.
  // The header location pill + the PLP/cart EDD read `userLocation` (localStorage), but the cart's
  // delivery address lives in `order_shipping_address` (sessionStorage) — written by several paths
  // (the sticky-cart save AND the global checkout-address-handler's backend fetch), none of which
  // sync userLocation. So mirror the confirmed order address into userLocation + broadcast
  // `locationUpdated` (which the header + edd.js refreshPlpEdd already listen for). Reading
  // order_shipping_address (not the save event's formData) keeps it correct for every writer.
  // `force` overrides an existing header location (use on an explicit save); without it we only
  // fill when the header has none, so a deliberately-set header location isn't clobbered on open.
  function syncHeaderLocation(force) {
    var raw = sessionStorage.getItem('order_shipping_address') || sessionStorage.getItem('guestAddress');
    if (!raw) return;
    var a; try { a = JSON.parse(raw); } catch (e) { return; }
    if (!a || a.is_confirmed === false || !a.zip) return;
    var cur; try { cur = JSON.parse(localStorage.getItem('userLocation')); } catch (e) {}
    if (cur && cur.pincode === a.zip) return;        // already in sync → nothing to do
    if (!force && cur && cur.pincode) return;        // passive: only fill when header has none
    try {
      localStorage.setItem('userLocation', JSON.stringify({
        name: a.name || '', phone: a.phone || '',
        house: a.address1 || '', area: a.address1 || '',
        pincode: a.zip || '', city: a.city || '', state: a.province || '',
        country: a.country || 'India',
        fullAddress: [a.address1, a.address2, a.city, a.province].filter(Boolean).join(', '),
        latitude: a.latitude || null, longitude: a.longitude || null,
        is_confirmed: true, source: 'sticky_cart', timestamp: Date.now(),
        addressId: a.id ? String(a.id) : undefined
      }));
    } catch (e) {}
    try { window.dispatchEvent(new CustomEvent('locationUpdated')); } catch (e) {}
  }

  function onAddressSaved(e) {
    var fd = (e && e.detail && e.detail.formData) || {};
    // The picker's formData (assets/address-map-ui.js collectFormData) uses these EXACT keys —
    // receiverFirstName/receiverLastName/receiverMobile, address, apartment, pincode, state,
    // coordinates.{lat,lng}. Reading first_name/phone/address1/zip/latitude (as before) left
    // name + phone + coords EMPTY, so the saved address never reflected the edit. Fallbacks keep
    // it working for other shapes (e.g. a backend address with first_name/address1/zip).
    var coords = fd.coordinates || {};
    var fName = fd.receiverFirstName || fd.first_name || '';
    var lName = fd.receiverLastName || fd.last_name || '';
    var addr = {
      id: (e && e.detail && e.detail.addressId) || null,
      name: fd.name || [fName, lName].filter(Boolean).join(' '),
      first_name: fName,
      last_name: lName,
      phone: fd.receiverMobile || fd.phone || '',
      address1: fd.address || fd.address1 || fd.area || '',
      address2: fd.apartment || fd.address2 || fd.landmark || fd.fullAddress || '',
      city: fd.city || '',
      province: fd.state || fd.province || '',
      zip: fd.pincode || fd.zip || '',
      country: fd.country || 'India',
      latitude: coords.lat || fd.latitude || fd.lat || null,
      longitude: coords.lng || fd.longitude || fd.lng || null,
      is_confirmed: true
    };
    try {
      sessionStorage.setItem('order_shipping_address', JSON.stringify(addr));
    } catch (err) {}
    // Mirror the just-saved address into userLocation so the header pill + EDD update immediately
    // (force=true: an explicit save replaces any existing header location).
    syncHeaderLocation(true);
    if (stickyFlowActive() && window.StickyCartAnalytics && typeof window.StickyCartAnalytics.addressSaved === 'function') {
      window.StickyCartAnalytics.addressSaved({ pincode: addr.zip || null, city: addr.city || null, state: addr.province || null, is_confirmed: true });
    }
    renderAddress();
    updateFooterForStep(currentStep);
    if (currentStep === 'address') goToStep('review');
  }

  function setSel(scope, sel, val) {
    var el = scope.querySelector(sel);
    if (el) el.textContent = val;
  }

  /* ---- Bind handlers on (re-)injected content ------------------------- */
  // innerHTML is replaced wholesale on every (re-)render, so old listeners are
  // discarded with the old DOM — re-binding per-element here is leak-free.
  function bindContent() {
    var i;

    var closeBtns = contentEl.querySelectorAll('[data-st-close]');
    for (i = 0; i < closeBtns.length; i++) {
      closeBtns[i].addEventListener('click', close);
    }
    // Header back: step back through the flow, or close when on the cart step.
    var backBtns = contentEl.querySelectorAll('[data-st-back]');
    for (i = 0; i < backBtns.length; i++) {
      backBtns[i].addEventListener('click', function () {
        var idx = stepIndex(currentStep);
        if (idx <= 0) { close(); return; }
        var prev = STEPS[idx - 1];
        // Address is modal-only now (never a landing screen) → Review goes back to Cart.
        if (prev === 'address') prev = 'cart';
        goToStep(prev);
      });
    }

    // Stepper: navigate to any step, forward or backward (forward applies the same gates as
    // Proceed — a confirmed address for Review/Payment, login for Payment).
    var stepEls = contentEl.querySelectorAll('[data-st-stepper] [data-step]');
    for (i = 0; i < stepEls.length; i++) {
      stepEls[i].addEventListener('click', function (e) {
        navigateToStep(e.currentTarget.getAttribute('data-step'));
      });
    }

    // Footer primary CTA advances the flow.
    var proceedBtn = contentEl.querySelector('[data-st-proceed]');
    if (proceedBtn) proceedBtn.addEventListener('click', onProceed);

    // Quantity − / + is handled via delegation (bound once in wireUp) so it also works
    // for the EDD module's async-cloned shipment-split rows (cloneNode drops listeners).

    // Line-item image → open the product's PDP (skips if already on that PDP).
    var pdpEls = contentEl.querySelectorAll('[data-st-pdp-url]');
    for (i = 0; i < pdpEls.length; i++) {
      pdpEls[i].addEventListener('click', onPdpClick);
    }

    // Footer "View detailed bill" → scroll the body to the price-details card.
    var billToggle = contentEl.querySelector('[data-st-bill-toggle]');
    if (billToggle) {
      billToggle.addEventListener('click', function () {
        var bill = contentEl.querySelector('[data-st-bill]');
        if (bill && bill.scrollIntoView) bill.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.billExpanded === 'function') {
          window.StickyCartAnalytics.billExpanded();
        }
      });
    }

    bindAnalyticsImpressions();

    // OOS items don't block checkout — they're shown in a separate card and excluded
    // from every total/payload. Checkout is only disabled when there's nothing
    // purchasable (no in-stock units). Wire the OOS card's × to remove all OOS lines.
    var rootEl = contentEl.querySelector('[data-st-content-root]');
    var instockUnits = rootEl ? parseInt(rootEl.getAttribute('data-instock-units'), 10) : 0;
    var proceed = contentEl.querySelector('[data-st-proceed]');
    if (proceed && instockUnits === 0) {
      proceed.disabled = true;
      proceed.setAttribute('title', 'No in-stock items to check out');
    }
    var oosClear = contentEl.querySelector('[data-st-oos-clear]');
    if (oosClear) oosClear.addEventListener('click', clearOOS);

    // Empty-cart "Continue Shopping" just closes the drawer.
    var continueBtn = contentEl.querySelector('[data-st-continue-shopping]');
    if (continueBtn) continueBtn.addEventListener('click', close);

    // Rewards items are limited to qty 1 (cart-price.liquid:47) — normalise if needed.
    normalizeRewards();
    // BXGY badge copy comes from the applied coupon (localStorage.bxgyData).
    fillBxgyBadges();
    // Surprise-gift auto-add/remove (parity with cart-revamp addSurpriseGiftCart).
    if (window.StickyCartSurpriseGift && typeof window.StickyCartSurpriseGift.apply === 'function') {
      window.StickyCartSurpriseGift.apply(contentEl, { onChange: function () { refresh(); fetchCartThenBroadcast(); } });
    }

    // Delivery (EDD) grouping + gift-unlock carousel — enhancement modules that
    // run after each (re-)render. They no-op gracefully if unavailable.
    if (window.StickyCartEDD && typeof window.StickyCartEDD.apply === 'function') {
      window.StickyCartEDD.apply(contentEl);
    }
    // Gift claim / Rx attach mutate the cart outside changeLine — re-render the
    // drawer AND broadcast so PDP/PLP/header stay in sync.
    var refreshAndBroadcast = function () { refresh(); fetchCartThenBroadcast(); };

    if (window.StickyCartGifts && typeof window.StickyCartGifts.apply === 'function') {
      window.StickyCartGifts.apply(contentEl, { onClaim: refreshAndBroadcast });
    }
    // Fees + Total (live /order/fees) — owns the Total Amount / footer / savings.
    if (window.StickyCartFees && typeof window.StickyCartFees.apply === 'function') {
      window.StickyCartFees.apply(contentEl);
    }
    if (window.StickyCartCoupon && typeof window.StickyCartCoupon.apply === 'function') {
      window.StickyCartCoupon.apply(contentEl, { onChange: refresh });
    }
    // Re-validate the cart against /cart/handle-cart on every render/mutation (parity
    // with the cart page's per-reload validation): refresh cartId, drop hide_from_frontend
    // items, and re-validate/recompute the applied coupon for the new line items.
    if (window.StickyCartCoupon && typeof window.StickyCartCoupon.reconcile === 'function') {
      window.StickyCartCoupon.reconcile(contentEl, { onChange: refreshAndBroadcast });
    }
    if (window.StickyCartRx && typeof window.StickyCartRx.apply === 'function') {
      window.StickyCartRx.apply(contentEl, { onChange: refreshAndBroadcast });
    }
    // Warm the cart cache in the background so Proceed → Review is instant (no /cart.js round-trip
    // on the click). Pure cache fill — never re-renders, so it adds no extra updates.
    if (window.StickyCartCheckout && typeof window.StickyCartCheckout.prewarmCart === 'function') {
      window.StickyCartCheckout.prewarmCart();
    }

    // Apply the active step last (stepper state + visible panel + footer CTA).
    applyStep(currentStep);
  }

  // Fire the per-render "Viewed" impression events (Bill Details, OOS Banner, Offer Strips).
  // The hub emitters self-dedupe per open (fireOnce), so re-binding on each render is safe.
  function bindAnalyticsImpressions() {
    var A = window.StickyCartAnalytics;
    if (!A) return;
    if (typeof A.billDetailsViewed === 'function' && contentEl.querySelector('[data-st-bill]')) {
      A.billDetailsViewed();
    }
    var oosCard = contentEl.querySelector('[data-st-oos]');
    if (oosCard && typeof A.oosBannerDisplayed === 'function') {
      var oosNodes = contentEl.querySelectorAll('[data-st-oos-items] .st-cart__item');
      var oosProducts = [];
      for (var i = 0; i < oosNodes.length; i++) {
        oosProducts.push({
          product_id: oosNodes[i].getAttribute('data-product-id'),
          variant_id: oosNodes[i].getAttribute('data-variant-id'),
          sku_id: oosNodes[i].getAttribute('data-skuid'),
          product_name: (oosNodes[i].querySelector('.st-cart__item-name') || {}).textContent || null
        });
      }
      A.oosBannerDisplayed(oosProducts.length, oosProducts);
    }
    var offers = contentEl.querySelectorAll('[data-st-offers] .st-cart__offer');
    Array.prototype.forEach.call(offers, function (el, idx) {
      var img = el.querySelector('img');
      var text = (img && (img.getAttribute('alt') || img.getAttribute('src'))) || ('offer_' + idx);
      if (typeof A.observeImpression === 'function' && typeof A.offerStripViewed === 'function') {
        A.observeImpression(el, function () { A.offerStripViewed(text, idx, 'banner'); });
      }
      el.addEventListener('click', function () {
        if (typeof A.offerStripClicked === 'function') A.offerStripClicked(text, idx, 'banner');
      });
    });
  }

  // Line-item image click → navigate to the product's PDP. No-op if we're already on it.
  function onPdpClick(e) {
    var url = e.currentTarget.getAttribute('data-st-pdp-url');
    if (!url) return;
    try {
      if (location.pathname === new URL(url, location.origin).pathname) return;
    } catch (err) {}
    window.location.href = url;
  }

  // Quantity − / +. Reached via delegation (see wireUp) so it also fires for the EDD
  // module's async-cloned shipment-split rows, whose cloned buttons carry no listeners.
  function handleQty(btn, delta) {
    // OOS / inventory-maxed / special-price rows disable +; never increase an
    // unavailable line — surface the "limited quantity" toast instead.
    if (delta > 0 && (btn.disabled || btn.getAttribute('aria-disabled') === 'true')) {
      toast('Sorry, we have limited quantity available for this item!');
      return;
    }
    var lineKey = btn.getAttribute('data-line-key');
    var row = btn.closest('.st-cart__item');
    // On a shipment-split line the displayed qty is the partial (per-shipment) amount;
    // every split row carries data-line-qty (the TRUE whole-line qty) so +/- on ANY row
    // edits the full cart line, then the drawer re-renders and re-splits.
    var lineQtyAttr = btn.getAttribute('data-line-qty');
    var current;
    if (lineQtyAttr != null && lineQtyAttr !== '') {
      current = parseInt(lineQtyAttr, 10);
    } else {
      var numEl = row ? row.querySelector('[data-st-qty-num]') : null;
      current = numEl ? parseInt(numEl.textContent, 10) : 0;
    }
    if (isNaN(current)) current = 0;
    var next = current + delta;
    if (next < 0) next = 0;
    // Spec: Remove Initiated when − is tapped at qty 1 (direct removal, no dialog).
    if (delta < 0 && current === 1 && window.StickyCartAnalytics &&
        typeof window.StickyCartAnalytics.removeInitiated === 'function') {
      window.StickyCartAnalytics.removeInitiated(row);
    }
    changeLine(lineKey, next, { el: row, prev: current, next: next });
  }

  /* ---- Entry hook on the header cart icon ------------------------------ */
  // header-icons.liquid is rendered by multiple header sections, so there are
  // several #HeaderCartTrigger elements (duplicate IDs) and more may be injected
  // dynamically. Delegate from document in the capture phase and match the cart
  // anchor by its stable aria-controls hook so EVERY instance is covered and we
  // run before any other cart-icon handler.
  function bindTrigger() {
    document.addEventListener(
      'click',
      function (e) {
        var target = e.target;
        if (!target || !target.closest) return;
        // Never hijack clicks inside the drawer itself.
        if (root.contains(target)) return;
        // The header cart icon, OR any "Go to cart" link (the add-to-cart toast's
        // GO TO CART, sticky ATC bars, etc.) — all open the drawer when enabled.
        var trigger =
          target.closest(cfg.triggerSel) ||
          target.closest('a[href="/cart"], a[href^="/cart?"]');
        if (!trigger) return;
        // Mobile or theme-off: let the normal /cart navigation happen.
        if (!isDesktop() || !cfg.flagDefault) return;
        if (flagResolved) {
          // GrowthBook decided: variant → open drawer; control → fall through to /cart.
          if (enabled) { e.preventDefault(); e.stopPropagation(); open(); }
          return;
        }
        // Decision not in yet → don't let the click redirect to /cart; hold it, then act on
        // the resolved value (open the drawer for the variant, navigate for control).
        e.preventDefault();
        e.stopPropagation();
        var href = (trigger.getAttribute && trigger.getAttribute('href')) || '/cart';
        (flagPromise || Promise.resolve()).then(function () {
          if (enabled && isDesktop()) open();
          else window.location.href = href;
        });
      },
      true // capture
    );
  }

  /* ---- Wire-up --------------------------------------------------------- */
  if (scrimEl) scrimEl.addEventListener('click', close);

  // Quantity − / + via delegation on the stable content container (its innerHTML is
  // swapped on every render, but the element itself persists, so this binds once).
  // Delegation also covers the EDD module's async-cloned shipment-split rows.
  if (contentEl) {
    contentEl.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var inc = e.target.closest('[data-st-qty-inc]');
      if (inc) { handleQty(inc, 1); return; }
      var dec = e.target.closest('[data-st-qty-dec]');
      if (dec) { handleQty(dec, -1); return; }
    });
  }

  // The global address picker (window.showAddressMap) fires this on save.
  window.addEventListener('addressSavedSuccessfully', onAddressSaved);

  // The OTHER address flows don't fire addressSavedSuccessfully and don't reload the
  // page: pincode change, Google-place pick, and saved-address selection in the manual
  // modal instead fire locationUpdated / addressSelectedFromModal / custom:pin-update
  // (snippets/pincode-overlay.liquid). Without handling these, the drawer's address
  // stayed stale until a reload. The new address is already in sessionStorage by the
  // time they fire, so just re-render from storage (+ recompute EDD/fees if open).
  // A SINGLE address change makes the global picker fire a BURST of events in one tick —
  // addressSavedSuccessfully (→ onAddressSaved → syncHeaderLocation → locationUpdated), PLUS the
  // picker's own locationUpdated / addressSelectedFromModal / custom:pin-update. Each used to call
  // refresh() independently, so coupons / fees / price re-rendered 3–4× per change. Coalesce the
  // burst into ONE refresh.
  var locRefreshT = null;
  function onLocationChanged() {
    if (!contentLoaded) return; // nothing rendered yet → next open fetches fresh
    if (root.hidden) { cartDirty = true; return; } // changed while closed → refresh on next open
    if (locRefreshT) clearTimeout(locRefreshT);
    locRefreshT = setTimeout(function () {
      locRefreshT = null;
      if (root.hidden || !contentLoaded) return;
      renderAddress();
      updateFooterForStep(currentStep);
      refresh(); // re-run EDD / fees / coupon ONCE for the new location
    }, 200);
  }
  window.addEventListener('locationUpdated', onLocationChanged);
  window.addEventListener('addressSelectedFromModal', onLocationChanged);
  document.addEventListener('custom:pin-update', onLocationChanged);

  // Spec address events (observable-only): derive from the picker's own dispatches.
  // Only while a sticky-cart flow is active (drawer open or address picker opened by us).
  window.addEventListener('addressSelectedFromModal', function (e) {
    if (!stickyFlowActive()) return;
    var A = window.StickyCartAnalytics;
    if (A && typeof A.savedAddressSelected === 'function') A.savedAddressSelected((e && e.detail) || {});
  });
  window.addEventListener('locationUpdated', function (e) {
    if (!stickyFlowActive()) return;
    var A = window.StickyCartAnalytics; var d = (e && e.detail) || {};
    if (!A) return;
    if (d.source === 'geolocation') {
      if (typeof A.useCurrentLocation === 'function') A.useCurrentLocation('granted', 'address_list');
      if (typeof A.permissionGranted === 'function') A.permissionGranted(d.latitude || null, d.longitude || null, d.pincode || null);
    } else if (d.source === 'google_places') {
      if (typeof A.mapConfirmed === 'function') A.mapConfirmed({ latitude: d.latitude || null, longitude: d.longitude || null, pincode: d.pincode || null, city: d.city || null, state: d.state || null });
    }
  });

  // Fallback: some location picks (e.g. choosing a saved address in the manual
  // modal) may not fire addressSavedSuccessfully. The modal returns focus to the
  // page on close, so re-evaluate the footer then — flips prompt → confirmed once
  // an address has been stored. No-op unless the drawer is open.
  function refreshFooterIfOpen() {
    if (!root.hidden && contentLoaded) updateFooterForStep(currentStep);
  }
  window.addEventListener('focus', refreshFooterIfOpen);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshFooterIfOpen();
  });

  // External cart changes (PDP/PLP add, cart-page edits) only happen while the
  // drawer is CLOSED — its scrim blocks the page when open. Mark dirty so the
  // next open refreshes. Gating on root.hidden also ignores the drawer's OWN
  // outbound cart:updated (fired while open) → no self-trigger loop.
  function markDirty() {
    if (root.hidden) cartDirty = true;
  }
  document.addEventListener('cart:updated', markDirty);
  document.addEventListener('ajaxProduct:added', markDirty);

  // Auto-open the drawer after a successful ADD-to-cart (PDP/PLP/quick-add), making the
  // sticky cart the post-add surface (this is also spec event #1's trigger). Precise gating:
  //   • only the desktop variant (enabled), so control + mobile keep their flows;
  //   • only when the drawer is CLOSED — so the drawer's OWN adds (gift claim, BXGY,
  //     surprise-gift) never re-open it, and rapid double-signals open it once
  //     (open() flips root.hidden synchronously, so the 2nd signal is a no-op);
  //   • never on the cart/checkout pages (their own UIs own those surfaces).
  // cartDirty is forced so open() re-fetches and shows the just-added line.
  function autoOpenOnAdd() {
    if (!enabled || !isDesktop() || !root.hidden) return;
    var path = location.pathname || '';
    if (path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0) return;
    cartDirty = true;
    open();
  }

  // When the drawer is ALREADY open and more products are added (rapid multi-add, or adds
  // whose /cart/add requests finish AFTER the drawer opened), the first open fetched the
  // section with a stale cart — so only the first product showed. Debounce-refresh the open
  // drawer to the FINAL cart. Waits out an in-flight first-open fetch (contentLoaded) or the
  // drawer's own mutation (busy); capped so a failed fetch can't loop forever.
  var addRefreshT, addRefreshTries;
  function refreshOpenForAdd() {
    clearTimeout(addRefreshT);
    addRefreshTries = 0;
    addRefreshT = setTimeout(function tryRefresh() {
      if (root.hidden) return; // user closed it → next open refetches anyway
      if ((!contentLoaded || busy) && addRefreshTries++ < 8) { addRefreshT = setTimeout(tryRefresh, 250); return; }
      if (contentLoaded && !busy) refresh();
    }, 250);
  }

  // External add (PLP/PDP/quick-add fire `ajaxProduct:added`; the drawer's own gift/BXGY adds
  // do NOT, so they never trigger this): open when closed, refresh-to-latest when already open.
  function onExternalAdd() {
    if (!enabled || !isDesktop()) return;
    var path = location.pathname || '';
    if (path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0) return;
    if (root.hidden) autoOpenOnAdd();
    else refreshOpenForAdd();
  }
  document.addEventListener('ajaxProduct:added', onExternalAdd);

  // A prepaid order sets stOrderSource before redirecting to Juspay; page.checking-payment
  // consumes + clears it on a CONFIRMED order. If the user abandons payment and returns
  // (back button / bfcache), they land on a normal page instead — clear the stale flag here
  // so a later order can never mis-fire "Sticky Cart Order Placed". pageshow fires on fresh
  // loads AND bfcache restores; it only fires on this page when the user navigates BACK
  // (the forward success path goes to checking-payment, excluded below), so the legit flow
  // is untouched.
  function clearStaleOrderFlag() {
    if ((location.pathname || '').indexOf('checking-payment') > -1) return; // its consumer
    try {
      sessionStorage.removeItem('stOrderSource');
      sessionStorage.removeItem('stOrderCoupon');
      sessionStorage.removeItem('stOrderCouponDiscount');
      sessionStorage.removeItem('stOrderPaymentMethod');
    } catch (e) {}
  }
  window.addEventListener('pageshow', clearStaleOrderFlag);

  // Some mutations fire NO event — notably a PLP quantity DECREASE in the Findify
  // grid (assets/edd.js skips ajaxProduct:added for decreases/removals). Catch every
  // cart mutation at the network layer too (same approach as sticky-atc.liquid) so
  // the drawer still refreshes on next open. markDirty() is gated on root.hidden, so
  // the drawer's OWN mutations (made while open) never mark it dirty → no loop.
  var CART_MUTATION = /\/cart\/(change|add|update|clear)(\.js)?/;
  var CART_ADD = /\/cart\/add(\.js)?(\?|$)/; // add specifically → auto-open
  if (window.fetch && !window.__stickyCartFetchPatched) {
    window.__stickyCartFetchPatched = true;
    var stOrigFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = stOrigFetch(input, init);
      if (CART_MUTATION.test(url)) {
        p.then(function (r) {
          markDirty();
          // Only auto-open on a SUCCESSFUL add (covers quick-adds that don't fire ajaxProduct:added).
          if (CART_ADD.test(url) && (!r || r.ok !== false)) autoOpenOnAdd();
        }).catch(function () {});
      }
      return p;
    };
  }
  // Mirror sticky-atc's jQuery fallback for code paths that mutate via $.ajax.
  if (window.jQuery && !window.__stickyCartAjaxPatched) {
    window.__stickyCartAjaxPatched = true;
    window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
      if (settings && settings.url && CART_MUTATION.test(settings.url)) {
        markDirty();
        if (CART_ADD.test(settings.url)) autoOpenOnAdd();
      }
    });
  }

  // Hard kill-switch: theme setting OFF (cfg.flagDefault) → never consult GrowthBook,
  // so no `Experiment Viewed` exposure fires. Desktop-only: mobile visitors can't see
  // the drawer, so they must not be bucketed/exposed (keeps the experiment population
  // to users who can actually experience the variant).
  if (cfg.flagDefault && isDesktop()) {
    flagPromise = resolveFlag().then(function (v) {
      enabled = !!v;
      flagResolved = true;
      // Marker class lets CSS suppress the legacy add-to-cart popups (#HeaderCart toast /
      // nudge) on PLP/PDP — the sticky drawer is the cart entry point in this variant.
      if (enabled) document.documentElement.classList.add('st-sticky-cart-enabled');
      if (enabled) maybeRestore();
      return enabled;
    });
  }

  // After a login from the Review step (which reloads the page via multipass), reopen the
  // drawer straight to Review. The intent is a one-shot sessionStorage flag set before
  // login; it's always cleared here so an unrelated later reload never re-triggers.
  function maybeReopenAfterLogin() {
    var step;
    try { step = sessionStorage.getItem('stReopenStep'); } catch (e) { step = null; }
    if (!step) return;
    try { sessionStorage.removeItem('stReopenStep'); } catch (e) {}
    // Only reopen if the reload actually logged the user in and Review can render
    // (a confirmed delivery address is required; it persists in sessionStorage).
    if (step !== 'review' || !isLoggedIn() || !hasConfirmedOrderAddress()) return;
    open({ step: 'review' });
  }

  // On load, restore a drawer that was open on this exact page before a refresh / Back nav.
  // The post-login reopen (one-shot stReopenStep) takes precedence when both are present.
  function maybeRestore() {
    var loginStep = null;
    try { loginStep = sessionStorage.getItem('stReopenStep'); } catch (e) {}
    if (loginStep) { maybeReopenAfterLogin(); return; }
    var s = readOpenState();
    if (!s) return;
    open({ step: s.step || 'cart' });
    if (s.ipaVId) restoreIpaDrawer(s.ipaVId);
  }

  // Find the IPA/gift card for a variant id within the rendered drawer content.
  function findGiftCard(vId) {
    if (!contentEl) return null;
    var cards = contentEl.querySelectorAll('.st-cart__gift-card[data-variant-id]');
    for (var i = 0; i < cards.length; i++) {
      if (String(cards[i].getAttribute('data-variant-id')) === String(vId)) return cards[i];
    }
    return null;
  }

  // Reopen the IPA product drawer for a given variant once its card has rendered (the gift
  // carousel loads async via /offers). Clicking the card's opener reuses the existing wiring
  // (assets/sticky-cart-gift-unlock.js) so the drawer opens with the right item + onClaim.
  function restoreIpaDrawer(vId) {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var card = findGiftCard(vId);
      if (card) {
        clearInterval(timer);
        var opener = card.querySelector('.st-cart__gift-name') || card.querySelector('.st-cart__gift-imgbox');
        if (opener) opener.click();
      } else if (tries > 60) { // ~9s ceiling — card may be gone (claimed / out of offers)
        clearInterval(timer);
      }
    }, 150);
  }

  // The IPA drawer (assets/sticky-cart-ipa-drawer.js) tells us when it opens/closes so the
  // restored state includes which product drawer was showing.
  document.addEventListener('stcart:ipa-opened', function (e) {
    saveOpenState({ ipaVId: e && e.detail ? e.detail.vId : null });
  });
  document.addEventListener('stcart:ipa-closed', function () {
    saveOpenState({ ipaVId: null });
  });

  bindTrigger();

  // Hand off from the shell's early interceptor: from now on, the module owns trigger clicks.
  // If a cart-icon click was captured before this deferred script ran (the load-time gap),
  // act on it now — open the drawer once the flag resolves, or fall through to /cart.
  window.__stReady = true;
  if (window.__stEarlyClick) {
    var earlyHref = window.__stEarlyClick;
    window.__stEarlyClick = null;
    if (!isDesktop() || !cfg.flagDefault) {
      window.location.href = earlyHref;
    } else {
      (flagPromise || Promise.resolve()).then(function () {
        if (enabled && isDesktop()) open();
        else window.location.href = earlyHref;
      });
    }
  }

  // Filter a /cart.js items array down to in-stock lines (used by checkout / coupon /
  // gift-unlock so OOS items never reach price/coupon/IPA/payment payloads).
  function filterInStock(items) {
    var oos = getOOSKeys();
    if (!oos.length) return items || [];
    return (items || []).filter(function (it) { return oos.indexOf(String(it.key)) < 0; });
  }

  // Lightweight toast (e.g. gift "limited quantity available" notice — Figma 416-11641).
  // Fixed to the viewport, above the drawer + IPA drawer; auto-dismisses.
  var TOAST_INFO_SVG =
    `<svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.1067 2.18408C10.9465 2.18408 8.83481 2.82465 7.03868 4.02479C5.24255 5.22492 3.84264 6.93072 3.01597 8.92647C2.1893 10.9222 1.97301 13.1183 2.39444 15.237C2.81587 17.3556 3.8561 19.3018 5.38358 20.8293C6.91106 22.3567 8.85719 23.397 10.9759 23.8184C13.0945 24.2398 15.2906 24.0235 17.2864 23.1969C19.2821 22.3702 20.9879 20.9703 22.188 19.1742C23.3882 17.378 24.0288 15.2664 24.0288 13.1062C24.0288 11.6719 23.7462 10.2516 23.1974 8.92647C22.6485 7.60134 21.844 6.3973 20.8297 5.38309C19.8155 4.36888 18.6115 3.56436 17.2864 3.01548C15.9612 2.46659 14.541 2.18408 13.1067 2.18408ZM14.1989 17.475C14.1989 17.7647 14.0838 18.0425 13.879 18.2473C13.6741 18.4521 13.3963 18.5672 13.1067 18.5672C12.817 18.5672 12.5392 18.4521 12.3344 18.2473C12.1295 18.0425 12.0145 17.7647 12.0145 17.475V12.014C12.0145 11.7243 12.1295 11.4465 12.3344 11.2417C12.5392 11.0368 12.817 10.9218 13.1067 10.9218C13.3963 10.9218 13.6741 11.0368 13.879 11.2417C14.0838 11.4465 14.1989 11.7243 14.1989 12.014V17.475ZM13.1067 9.82955C12.8906 9.82955 12.6795 9.76549 12.4999 9.64548C12.3203 9.52546 12.1803 9.35488 12.0976 9.15531C12.0149 8.95573 11.9933 8.73612 12.0354 8.52426C12.0776 8.31239 12.1816 8.11778 12.3344 7.96503C12.4871 7.81228 12.6817 7.70826 12.8936 7.66611C13.1055 7.62397 13.3251 7.6456 13.5246 7.72827C13.7242 7.81093 13.8948 7.95092 14.0148 8.13054C14.1348 8.31015 14.1989 8.52132 14.1989 8.73734C14.1989 9.02701 14.0838 9.30482 13.879 9.50964C13.6741 9.71447 13.3963 9.82955 13.1067 9.82955Z" fill="#F48E3A" />
    </svg>`;


  function toast(msg, opts) {
    opts = opts || {};
    var t = document.createElement('div');
    t.className = 'st-cart__toast' + (opts.error ? ' st-cart__toast--error' : '');
    t.setAttribute('role', 'status');
    t.innerHTML = TOAST_INFO_SVG + '<span class="st-cart__toast-text"></span>';
    t.querySelector('.st-cart__toast-text').textContent = msg;
    document.body.appendChild(t);
    void t.offsetWidth; // reflow so the entrance transition runs
    t.classList.add('is-visible');
    setTimeout(function () {
      t.classList.remove('is-visible');
      setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 320);
    }, opts.duration || 3000);
  }

  window.StickyCart = {
    open: open,
    close: close,
    refresh: refresh,
    getOOSKeys: getOOSKeys,
    filterInStock: filterInStock,
    toast: toast,
    isEnabled: function () {
      return enabled && isDesktop();
    },
    // Resolves once the GrowthBook decision has landed (or immediately if there's nothing to
    // resolve — theme-off / mobile). Callers that branch on isEnabled() right after a user action
    // (e.g. reorder) await this first so an early click isn't mis-treated as control.
    whenReady: function () {
      return flagResolved ? Promise.resolve(enabled) : (flagPromise || Promise.resolve(enabled));
    }
  };
})();
